import {
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useClipboard,
  VStack,
} from "@chakra-ui/react";
import { createDialogContext, DialogProvider } from "../../Common/Dialog";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { KYDOrganizationEventLink } from "@common/interfaces/KYDOrganization";
import PermissionSelector from "../EventDetail/Nav/Settings/Permissions";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import PhoneInput from "react-phone-input-2";
import { KYDScope } from "@common/interfaces/ICognito";

export const CreateTeamMemberDialogContext = createDialogContext();

const messageFrom = (err: unknown) => {
  if (typeof err === "string" && err) {
    return err;
  }
  if (err && typeof err === "object" && "message" in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message) {
      return message;
    }
  }
  return "Something went wrong. Please try again.";
};

const CreateTeamMember = ({
  editingTeamMember,
  isLoading = false,
  onDelete,
  onInvite,
  onUpdate,
  onClose,
}: {
  isLoading?: boolean;
  editingTeamMember?: KYDOrganizationUser;
  onInvite?: (values: Partial<KYDOrganizationUser>) => Promise<void>;
  onUpdate?: (userId: string, scopes: KYDScope[]) => Promise<void>;
  onDelete: (item: KYDOrganizationUser) => Promise<void>;
  onClose: () => void;
}) => {
  const { isOpen, close } = useContext(CreateTeamMemberDialogContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  const requestIdRef = useRef(0);
  isOpenRef.current = isOpen;

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    control,
    formState: { isValid, isDirty, errors },
  } = useForm({
    mode: "all",
    criteriaMode: "all",
    values: {
      ...editingTeamMember,
      scopes: editingTeamMember ? editingTeamMember.scopes : [],
    },
  });

  const scopes = watch("scopes");
  const busy = isLoading || isSubmitting;
  const title = !editingTeamMember
    ? "Invite New Team Member"
    : editingTeamMember.first_name
      ? `Edit ${editingTeamMember.first_name}`
      : "Edit team member";
  const _onSubmit = async (values: Partial<KYDOrganizationEventLink>) => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    const requestId = requestIdRef.current;
    try {
      if (editingTeamMember) {
        await onUpdate(editingTeamMember.id, values.scopes);
      } else {
        const email = (values as { email?: string }).email?.trim();
        await onInvite({ ...values, email });
      }
    } catch (err) {
      if (isOpenRef.current && requestId === requestIdRef.current) {
        setSubmitError(messageFrom(err));
      }
    } finally {
      if (requestId === requestIdRef.current) {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    }
  };

  const _onDelete = async () => {
    if (!editingTeamMember || submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    const requestId = requestIdRef.current;
    try {
      await onDelete(editingTeamMember);
    } catch (err) {
      if (isOpenRef.current && requestId === requestIdRef.current) {
        setSubmitError(messageFrom(err));
      }
    } finally {
      if (requestId === requestIdRef.current) {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    }
  };

  const _onClose = () => {
    requestIdRef.current += 1;
    isOpenRef.current = false;
    submittingRef.current = false;
    setIsSubmitting(false);
    reset();
    setSubmitError(null);
    close();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      requestIdRef.current += 1;
      submittingRef.current = false;
      setIsSubmitting(false);
      reset();
      setSubmitError(null);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      isCentered
      closeOnOverlayClick={!busy}
      closeOnEsc={!busy}
      onClose={() => {
        _onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent maxW="3xl">
        <ModalHeader paddingTop={4}>
          <Text w="full">{title}</Text>
        </ModalHeader>
        <ModalBody>
          <VStack w="full" spacing={2}>
            <VStack mb={4} w="full">
              {!editingTeamMember ? (
                <Stack spacing={4} w="100%">
                  <HStack>
                    <Stack spacing={0} w="50%">
                      <Text fontWeight={"medium"}>First Name</Text>
                      <Input
                        {...register("first_name", {
                          required: true,
                        })}
                        isDisabled={!!editingTeamMember}
                        placeholder="First Name"
                      />
                    </Stack>
                    <Stack spacing={0} w="50%">
                      <Text fontWeight={"medium"}>Last Name</Text>
                      <Input
                        {...register("last_name", {
                          required: true,
                        })}
                        isDisabled={!!editingTeamMember}
                        placeholder="Last Name"
                      />
                    </Stack>
                  </HStack>
                  <Stack spacing={0} w="100%">
                    <Text fontWeight={"medium"}>Email</Text>
                    <Input
                      {...register("email", {
                        required: "Email is required",
                        validate: (value) =>
                          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                            `${value || ""}`.trim()
                          ) || "Enter a valid email address",
                      })}
                      isDisabled={!!editingTeamMember}
                      placeholder="Email"
                    />
                    {errors.email?.message ? (
                      <Text color="red.500" fontSize="sm">
                        {String(errors.email.message)}
                      </Text>
                    ) : null}
                  </Stack>
                  <Stack spacing={0} w="100%">
                    <Text fontWeight={"medium"}>Phone Number</Text>
                    <HStack>
                      <Controller
                        name="phone_number"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                          <PhoneInput
                            value={value}
                            onChange={onChange}
                            autoFormat={true}
                            inputProps={{
                              placeholder: "Mobile number",
                            }}
                            inputStyle={{
                              width: "100%",
                              height: "60px",
                              fontFamily: "Work sans",
                              borderWidth: "1px",
                              borderRadius: "5px",
                            }}
                            countryCodeEditable={false}
                            country={"us"}
                          />
                        )}
                      />
                    </HStack>
                  </Stack>
                </Stack>
              ) : null}
            </VStack>
            <PermissionSelector
              mode="team"
              scopes={scopes}
              onChange={(values) =>
                setValue("scopes", values.scopes, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
            {submitError ? (
              <Text color="red.500" fontSize="sm" w="full">
                {submitError}
              </Text>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr="4" isDisabled={busy} onClick={_onClose}>
            Close
          </Button>
          {editingTeamMember ? (
            <HStack>
              <Button
                bg="red.500"
                color="white"
                isDisabled={busy}
                isLoading={busy}
                onClick={_onDelete}
              >
                Delete
              </Button>
              <Button
                bg="green.500"
                color="white"
                isDisabled={busy || !isDirty}
                isLoading={busy}
                onClick={() => _onSubmit({ scopes })}
              >
                Save
              </Button>
            </HStack>
          ) : (
            <Button
              bg="green.500"
              color="white"
              isDisabled={busy || !isValid}
              isLoading={busy}
              onClick={handleSubmit(_onSubmit)}
            >
              Invite
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withCreateTeamMemberDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={CreateTeamMemberDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default CreateTeamMember;
